"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  TREATY_FIELDS as F,
  formatTreaty,
  type TreatyLabels,
  treatyIsReady,
} from "@/lib/tool-kit/legal-sources/treaty-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const TREATY_CITATION_GENERATOR_STRINGS = {
  formHeading: "Treaty details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Enter the treaty's name and the date it was signed or opened for signature to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  nameLabel: "Name of the treaty or convention",
  namePlaceholder: "Convention on the Rights of the Child",
  nameHint: "The official name, as the treaty series or depositary gives it.",
  shortTitleLabel: "Short title for in-text citations (optional)",
  shortTitlePlaceholder: "Refugee Convention",
  kindLabel: "Type",
  kindMultilateral: "Multilateral (many parties)",
  kindBilateral: "Bilateral (two parties)",
  partiesLabel: "Parties, for a bilateral treaty (Chicago)",
  partiesPlaceholder: "U.S.-Mex.",
  partiesHint:
    "Abbreviate and hyphenate as the Bluebook does, e.g. U.S.-Mex. or U.K.-Fr.",
  dateLabel: "Date signed, adopted or opened for signature",
  datePlaceholder: "YYYY-MM-DD",
  articleLabel: "Article (optional, Chicago)",
  articlePlaceholder: "3",
  seriesLabel: "Treaty series (Chicago)",
  seriesNone: "None — cite the URL only",
  seriesUnts: "U.N.T.S. — United Nations Treaty Series",
  seriesUst: "U.S.T. — United States Treaties",
  seriesTias: "T.I.A.S. — Treaties and Other International Acts Series",
  seriesIlm: "I.L.M. — International Legal Materials",
  seriesStat: "Stat. — U.S. Statutes at Large (before 1950)",
  seriesLnts: "L.N.T.S. — League of Nations Treaty Series",
  seriesVolumeLabel: "Series volume",
  seriesVolumePlaceholder: "1577",
  seriesPageLabel: "First page (or T.I.A.S. number)",
  seriesPagePlaceholder: "3",
  websiteLabel: "Website where you read it (for MLA)",
  websitePlaceholder: "United Nations Treaty Collection",
  urlLabel: "URL",
  urlPlaceholder:
    "https://treaties.un.org/Pages/ViewDetails.aspx?src=TREATY&mtdsg_no=IV-11&chapter=4",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  apaNote:
    "APA 7 (§11.10) needs only the name, the signing or approval date and a URL. The reference ends with the URL — no final period.",
  mlaNote:
    "MLA styles a treaty's name in roman type, like a law, and describes the source at the end of the entry.",
  chicagoNote:
    "Chicago follows the Bluebook for treaties and cites them in notes only. For a treaty the United States joined, cite U.S.T. or T.I.A.S. before U.N.T.S.",
  example: {
    name: "Convention on the Rights of the Child",
    kind: "multilateral",
    date: "1989-11-20",
    series: "unts",
    seriesVolume: "1577",
    seriesPage: "3",
    website: "United Nations Treaty Collection",
    url: "https://treaties.un.org/Pages/ViewDetails.aspx?src=TREATY&mtdsg_no=IV-11&chapter=4",
  },
} as const;

type Strings = typeof TREATY_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.name,
      kind: "text",
      label: strings.nameLabel,
      placeholder: strings.namePlaceholder,
      hint: strings.nameHint,
      wide: true,
    },
    {
      id: F.kind,
      kind: "select",
      label: strings.kindLabel,
      options: [
        { value: "multilateral", label: strings.kindMultilateral },
        { value: "bilateral", label: strings.kindBilateral },
      ],
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.parties,
      kind: "text",
      label: strings.partiesLabel,
      placeholder: strings.partiesPlaceholder,
      hint: strings.partiesHint,
    },
    {
      id: F.article,
      kind: "text",
      label: strings.articleLabel,
      placeholder: strings.articlePlaceholder,
    },
    {
      id: F.series,
      kind: "select",
      label: strings.seriesLabel,
      options: [
        { value: "none", label: strings.seriesNone },
        { value: "unts", label: strings.seriesUnts },
        { value: "ust", label: strings.seriesUst },
        { value: "tias", label: strings.seriesTias },
        { value: "ilm", label: strings.seriesIlm },
        { value: "stat", label: strings.seriesStat },
        { value: "lnts", label: strings.seriesLnts },
      ],
    },
    {
      id: F.seriesVolume,
      kind: "text",
      label: strings.seriesVolumeLabel,
      placeholder: strings.seriesVolumePlaceholder,
    },
    {
      id: F.seriesPage,
      kind: "text",
      label: strings.seriesPageLabel,
      placeholder: strings.seriesPagePlaceholder,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
    },
    {
      id: F.website,
      kind: "text",
      label: strings.websiteLabel,
      placeholder: strings.websitePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function treatyLabels(strings: Strings): TreatyLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function TreatyCitationGeneratorWidget() {
  const strings = useToolUi(TREATY_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => treatyLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatTreaty(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={treatyIsReady}
      strings={strings}
    />
  );
}
